import {
  WorkspaceConflictError,
  WorkspaceRepository,
  type WorkspaceCatalog,
  type WorkspacePayload,
  type WorkspaceStorage,
  type WorkspaceStore,
} from 'openalgo-charts/workspace';
import type { LayoutTarget, Widget, WidgetTranslator } from 'openalgo-charts/widget';
import { profileService } from '../../services/profileService';
import { sanitizeSessionShade, type SessionShade } from './savedChart';

/**
 * One catalog of candle-chart layouts and indicator groups for the account.
 * Django merges this object into `frontend_state`. Arrays in the catalog
 * replace, so a full catalog write replaces the saved list.
 * The check against `revision` is not a row lock: two tabs can both pass it.
 */
const CHART_WORKSPACE_KEY = 'chartWorkspace';
const NAMESPACE = 'steamroom';

const EXTENDED = 'qfExtendedHours';
const CROSSHAIR = 'qfCrosshair';
const SHADE_COLOR = 'qfShadeColor';
const SHADE_OPACITY = 'qfShadeOpacity';

/** Chart settings the widget does not keep inside a layout pane. */
export interface LayoutHostLook {
  extendedHours: boolean;
  crosshair: boolean;
  /** Null when this chart has not chosen a wash. */
  sessionShade: SessionShade | null;
}

const GROUP_COPY: Record<string, string> = {
  Templates: 'Groups',
  'Saved the template {name}': 'Saved the group {name}',
  'schema.ui.templates.title': 'Indicator groups',
  'schema.ui.templates.save': 'Save indicators as a group...',
  'schema.ui.templates.empty': 'No saved groups yet',
  'schema.ui.templates.loading': 'Loading groups',
  'schema.ui.templates.loadFailed': 'Groups could not be loaded',
  'schema.ui.templates.loadFailedWith': 'Groups could not be loaded: {error}',
  'schema.ui.templates.one': '1 indicator',
  'schema.ui.templates.count': '{count} indicators',
  'schema.ui.templates.replace': 'Replace',
  'schema.ui.templates.append': 'Add',
  'schema.ui.templates.replaceNamed': 'Replace the indicators with {name}',
  'schema.ui.templates.appendNamed': 'Add {name} to the indicators',
  'schema.ui.templates.applied': 'Applied {name}',
  'schema.ui.templates.nothingNew': '{name} added nothing new',
  'schema.ui.templates.nothing': 'Add an indicator to save a group',
  'schema.ui.templates.removed': 'Deleted {name}',
  'schema.ui.templates.removeFailed': '{name} could not be deleted: {error}',
  'schema.ui.templates.applyFailed': '{name} could not be applied: {error}',
  'schema.ui.templates.confirm': 'Delete {name}?',
  'schema.ui.templates.delete': 'Delete',
  'schema.ui.templates.deleteNamed': 'Delete {name}',
  'schema.ui.templates.keep': 'Keep',
  'schema.ui.templates.replay': 'Stop the replay to apply a group',
};

/** Indicator-group wording. Drawing-tool copy is left as the chart wrote it. */
export function chartWorkspaceTranslate(key: string, fallback: string): string | null {
  return GROUP_COPY[key] ?? GROUP_COPY[fallback] ?? null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function clonePayload(payload: WorkspacePayload): WorkspacePayload {
  return JSON.parse(JSON.stringify(payload)) as WorkspacePayload;
}

/**
 * Put the chart's own ticker on every pane. A saved layout keeps the
 * timeframe, the studies, and the chart settings, and leaves the name that
 * is already open.
 */
export function keepLayoutSymbol(payload: WorkspacePayload, symbol: string, exchange: string): WorkspacePayload {
  const next = clonePayload(payload);
  const ticker = symbol.trim();
  if (!ticker) return next;
  for (const pane of next.panes) {
    pane.symbol = ticker;
    pane.exchange = exchange;
  }
  return next;
}

/** Remember session, crosshair, and wash beside the widget's own chart state. */
export function stampLayoutHost(payload: WorkspacePayload, look: LayoutHostLook): WorkspacePayload {
  const next = clonePayload(payload);
  const pane = next.panes[0];
  if (!pane) return next;
  const settings: Record<string, string | number | boolean> = { ...pane.settings };
  settings[EXTENDED] = look.extendedHours;
  settings[CROSSHAIR] = look.crosshair;
  if (look.sessionShade) {
    settings[SHADE_COLOR] = look.sessionShade.color;
    settings[SHADE_OPACITY] = look.sessionShade.opacity;
  } else {
    delete settings[SHADE_COLOR];
    delete settings[SHADE_OPACITY];
  }
  pane.settings = settings;
  return next;
}

function shadeFrom(settings: Record<string, unknown>): SessionShade | null | undefined {
  if (!(SHADE_COLOR in settings) && !(SHADE_OPACITY in settings)) return undefined;
  return sanitizeSessionShade({
    color: settings[SHADE_COLOR],
    opacity: settings[SHADE_OPACITY],
  });
}

/** Host settings saved with the layout. Absent fields were not part of that save. */
export function readLayoutHost(payload: WorkspacePayload): {
  extendedHours?: boolean;
  crosshair?: boolean;
  sessionShade?: SessionShade | null;
} {
  const settings = payload.panes[0]?.settings;
  if (!isRecord(settings)) return {};
  const out: {
    extendedHours?: boolean;
    crosshair?: boolean;
    sessionShade?: SessionShade | null;
  } = {};
  if (typeof settings[EXTENDED] === 'boolean') out.extendedHours = settings[EXTENDED];
  if (typeof settings[CROSSHAIR] === 'boolean') out.crosshair = settings[CROSSHAIR];
  const shade = shadeFrom(settings);
  if (shade !== undefined) out.sessionShade = shade;
  return out;
}

/**
 * Layout apply keeps the ticker on the chart. Capture still records the
 * ticker the library stores; apply overwrites it before the chart restores.
 */
export function keepSymbolLayoutTarget(
  widget: Widget,
  inner: LayoutTarget,
  look: () => LayoutHostLook,
  applyLook: (saved: ReturnType<typeof readLayoutHost>) => void,
): LayoutTarget {
  return {
    capture() {
      return stampLayoutHost(inner.capture(), look());
    },
    apply(payload) {
      const symbol = widget.symbol();
      if (!symbol.trim()) return { applied: false, reason: 'This chart has no ticker yet' };
      const saved = readLayoutHost(payload);
      const next = keepLayoutSymbol(payload, symbol, widget.exchange());
      const report = inner.apply(next);
      if (report.applied) applyLook(saved);
      return report;
    },
    subscribe(listener) {
      return inner.subscribe?.(listener) ?? (() => undefined);
    },
    suspended() {
      return inner.suspended?.() ?? false;
    },
  };
}

function statusOf(err: unknown): number | null {
  if (err == null || typeof err !== 'object' || !('status' in err)) return null;
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
}

/** A 409 from a catalog write is the library's conflict, so it reloads and retries. */
function rethrowWorkspaceWrite(err: unknown): never {
  if (statusOf(err) === 409) throw new WorkspaceConflictError();
  throw err;
}

/** Revision on a stored catalog. A missing catalog is revision 0, the first save. */
function storedCatalogRevision(raw: unknown): number {
  if (!isRecord(raw)) return 0;
  const revision = raw.revision;
  if (typeof revision !== 'number' || !Number.isInteger(revision) || revision < 0) return 0;
  return revision;
}

function createChartWorkspaceStorage(
  io: {
    read: () => Promise<unknown | null>;
    write: (catalog: WorkspaceCatalog, expectedRevision: number) => Promise<void>;
  },
): WorkspaceStorage {
  return {
    async read() {
      return io.read();
    },
    async write(_namespace, catalog, expectedRevision) {
      try {
        await io.write(catalog, expectedRevision);
      } catch (err) {
        rethrowWorkspaceWrite(err);
      }
    },
  };
}

function catalogFromState(state: unknown): unknown | null {
  if (!isRecord(state)) return null;
  return state[CHART_WORKSPACE_KEY] ?? null;
}

/**
 * Read and write the catalog on `frontend_state.chartWorkspace`.
 * A write whose expected revision is behind the stored one is a conflict.
 */
export function createFrontendStateWorkspaceStorage(
  state: {
    read: () => Promise<unknown>;
    write: (catalog: WorkspaceCatalog) => Promise<void>;
  },
): WorkspaceStorage {
  return createChartWorkspaceStorage({
    async read() {
      const stored = await state.read();
      return stored ?? null;
    },
    async write(catalog, expectedRevision) {
      const stored = await state.read();
      if (storedCatalogRevision(stored) !== expectedRevision) throw new WorkspaceConflictError();
      await state.write(catalog);
    },
  });
}

let store: WorkspaceStore | null = null;

/** Shared by every candle chart on the page. The first menu reads the account. */
export function chartWorkspaceStore(): WorkspaceStore {
  if (store) return store;
  store = new WorkspaceRepository(
    createFrontendStateWorkspaceStorage({
      async read() {
        const body = await profileService.getFrontendState();
        return catalogFromState(body.frontend_state);
      },
      async write(catalog) {
        await profileService.saveFrontendState({ [CHART_WORKSPACE_KEY]: catalog });
      },
    }),
    NAMESPACE,
  );
  return store;
}

/**
 * Groups come from the workspace store. `layouts: false` keeps the library
 * from opening the last layout on every chart when the page loads.
 */
export function chartWidgetWorkspaceOptions(): {
  workspaces: WorkspaceStore;
  layouts: false;
  translate: WidgetTranslator;
} {
  return {
    workspaces: chartWorkspaceStore(),
    layouts: false,
    translate: (key, fallback) => chartWorkspaceTranslate(key, fallback),
  };
}

/** Tests build a fresh repository. The page keeps one for the session. */
export function resetChartWorkspaceStoreForTests(): void {
  store = null;
}
